import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLang } from '@/i18n/useLocalizedPath';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Sparkles,
  ChevronDown,
  ExternalLink
} from 'lucide-react';
import { trackEvent } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AskJigsawChat() {
  const { t } = useTranslation();
  const lang = useLang();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Seed (or refresh) the greeting in the current language while no real conversation exists
  useEffect(() => {
    setMessages(prev => (prev.length <= 1 ? [{ role: 'assistant', content: t('chat.greeting') }] : prev));
  }, [t]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleOpen = () => {
    setIsOpen(true);
    trackEvent('chatbot_opened', { page: window.location.pathname });
  };

  const handleClose = () => {
    setIsOpen(false);
    trackEvent('chatbot_closed', { messageCount: messages.length });
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    trackEvent('chatbot_message_sent', { 
      messageLength: userMessage.length,
      messageCount: messages.length + 1
    });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/ai/chat`, {
        message: userMessage,
        conversationHistory: messages.slice(-6).map(m => ({
          role: m.role,
          content: m.content
        })),
        lang: lang
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.message,
        suggestedActions: response.data.suggestedActions
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t('chat.errorMsg')
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderMessage = (content) => {
    // Convert markdown-style links to actual links
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }
      parts.push(
        <a
          key={match.index}
          href={match[2]}
          className="text-orange-600 hover:underline font-medium"
          onClick={() => trackEvent('chatbot_link_clicked', { url: match[2] })}
        >
          {match[1]}
        </a>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  // Floating button when closed
  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        className="fixed bottom-20 right-6 z-50 bg-orange-600 hover:bg-orange-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 flex items-center gap-2 group"
        aria-label={t('chat.buttonLabel')}
      >
        <Sparkles className="w-6 h-6" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
          {t('chat.buttonLabel')}
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-20 right-6 z-50 w-96 max-w-[calc(100vw-2rem)]">
      <Card className="shadow-2xl border-0 overflow-hidden">
        {/* Header */}
        <CardHeader className="bg-gradient-to-r from-orange-600 to-orange-500 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-full">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold">{t('chat.title')}</CardTitle>
                <p className="text-xs text-orange-100">{t('chat.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-white/20 text-white text-xs">
                AI
              </Badge>
              <button
                onClick={handleClose}
                className="hover:bg-white/20 p-1 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </CardHeader>

        {/* Messages */}
        <CardContent className="p-0">
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                    msg.role === 'user'
                      ? 'bg-orange-600 text-white rounded-br-md'
                      : 'bg-white text-gray-800 shadow-sm rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{renderMessage(msg.content)}</p>
                  
                  {/* Suggested actions */}
                  {msg.suggestedActions && (
                    <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-gray-100">
                      {msg.suggestedActions.map((action, i) => (
                        <a
                          key={i}
                          href={action.url}
                          className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded-full hover:bg-orange-100 flex items-center gap-1"
                          onClick={() => trackEvent('chatbot_action_clicked', { action: action.label })}
                        >
                          {action.label}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 shadow-sm rounded-2xl rounded-bl-md px-4 py-3">
                  <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('chat.inputPlaceholder')}
                className="flex-1"
                disabled={loading}
              />
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              {t('chat.poweredBy')}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
